import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	AlertTriangle,
	Home,
	Lock,
	Sparkles,
	X,
	Layout,
	Code,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from '@/hooks/useTranslation'
import { DashboardSkeleton } from '@/components/dashboard/DashboardSkeleton'
import { useProgramContext } from '@/context/ProgramContext'
import { useDashboardData } from '@/hooks/useDashboardData'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useTeamContext } from '@/context/TeamContext'
import { useQuery } from '@tanstack/react-query'
import { widgetApi } from '@/services/api'
import { useOptimisticProgram } from '@/hooks/useOptimisticProgram'
import { DashboardInbox } from '@/components/dashboard/DashboardInbox'
import { FeedbackTimelineChart } from '@/components/dashboard/FeedbackTimelineChart'

export default function Index() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { teamId } = useTeamContext()
	const {
		programId,
		currentProgram,
	} = useProgramContext()

	const optimisticProgram = useOptimisticProgram()
	const effectiveProgramId = useMemo(() => {
		return programId || optimisticProgram.programId
	}, [programId, optimisticProgram.programId])

	const { data, isLoading, error } = useDashboardData(
		effectiveProgramId,
		'all',
		teamId
	)

	const { data: widgetsData } = useQuery({
		queryKey: ['widgets', effectiveProgramId],
		queryFn: () => {
			if (!effectiveProgramId) {
				return Promise.resolve({ data: [], totalCount: 0 })
			}
			return widgetApi.getWidgets(effectiveProgramId)
		},
		enabled: !!effectiveProgramId,
	})

	const widgets = widgetsData?.data || []
	const hasWidgets = widgets.length > 0

	// Getting Started card state
	const [gettingStartedDismissed, setGettingStartedDismissed] = useState(() => {
		return (
			localStorage.getItem(
				`gettingStartedCardDismissed_program_${programId}`
			) === 'true'
		)
	})

	const [currentStep, setCurrentStep] = useState(() => {
		const savedStep = localStorage.getItem(
			`gettingStartedStep_program_${programId}`
		)
		return savedStep ? parseInt(savedStep) : 1
	})

	const handleDismissGettingStarted = () => {
		localStorage.setItem(
			`gettingStartedCardDismissed_program_${programId}`,
			'true'
		)
		setGettingStartedDismissed(true)
	}

	const handleStepAction = (step: number) => {
		// Track the action and move to next step
		if (step === 1) {
			navigate('/widgets/new')
			localStorage.setItem(`gettingStartedStep_program_${programId}`, '2')
			setCurrentStep(2)
		} else if (step === 2) {
			navigate('/docs')
			localStorage.setItem(`gettingStartedStep_program_${programId}`, '3')
			setCurrentStep(3)
		} else if (step === 3) {
			// Final step - mark as complete and dismiss
			handleDismissGettingStarted()
		}
	}

	// Reset state when program changes
	useEffect(() => {
		if (programId) {
			const dismissed =
				localStorage.getItem(
					`gettingStartedCardDismissed_program_${programId}`
				) === 'true'
			const savedStep = localStorage.getItem(
				`gettingStartedStep_program_${programId}`
			)
			setGettingStartedDismissed(dismissed)
			setCurrentStep(savedStep ? parseInt(savedStep) : 1)
		}
	}, [programId])


	if (isLoading && !data) return <DashboardSkeleton />

	if (error && !data)
		return (
			<div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
				<AlertTriangle className="w-16 h-16 text-destructive mb-4" />
				<h2 className="text-2xl font-semibold mb-2">{t('dashboard.errorLoadingData')}</h2>
				<p className="text-muted-foreground mb-4">
					{t('dashboard.couldNotFetchData')}
				</p>
			</div>
		)
	if (!data)
		return (
			<div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
				<Home className="w-16 h-16 text-muted-foreground mb-4" />
				<h2 className="text-2xl font-semibold mb-2">{t('dashboard.noDataAvailable')}</h2>
				<p className="text-muted-foreground">
					{t('dashboard.noDataToDisplay')}
				</p>
			</div>
		)

	const programNameToDisplay = currentProgram?.name || 'the selected program'

	const shouldShowGettingStarted =
		!gettingStartedDismissed && data?.totalFeedback === 0 && !hasWidgets && teamId && effectiveProgramId

	const steps = [
		{
			number: 1,
			title: t('dashboard.step1Title'),
			description: t('dashboard.step1Description'),
			buttonText: t('dashboard.createWidget'),
			buttonIcon: Layout,
			action: () => handleStepAction(1),
		},
		{
			number: 2,
			title: t('dashboard.step2Title'),
			description: t('dashboard.step2Description'),
			buttonText: t('dashboard.viewInstallationGuide'),
			buttonIcon: Code,
			action: () => handleStepAction(2),
		},
		{
			number: 3,
			title: t('dashboard.step3Title'),
			description: t('dashboard.step3Description'),
			buttonText: t('dashboard.startExploring'),
			buttonIcon: Sparkles,
			action: () => handleStepAction(3),
		},
	]

	const currentStepData = steps[currentStep - 1]

	return (
		<div className="h-full overflow-auto">
			<div className="space-y-6 sm:space-y-8 p-4 sm:p-6 md:p-8">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
				<div className="space-y-1">
					<h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
						{t('dashboard.dashboardOverview')}
					</h1>
					<p className="text-sm text-muted-foreground">
						{t('dashboard.summaryFor')}{' '}
						<span className="font-medium text-foreground">
							{currentProgram?.name || t('dashboard.yourProgram')}
						</span>
					</p>
				</div>
			</motion.div>

			{/* Getting Started Card - Progressive Steps */}
			{shouldShowGettingStarted && currentStepData && (
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
					className="mb-6">
					<Card>
						<CardHeader className="flex-row items-center justify-between pb-4">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-lg">
									{currentStep}
								</div>
								<div>
									<CardTitle className="text-lg">{t('dashboard.gettingStarted')}</CardTitle>
									<p className="text-xs text-muted-foreground mt-0.5">
										{t('dashboard.stepOf', { current: currentStep, total: steps.length })}
									</p>
								</div>
							</div>
							<Button
								variant="ghost"
								size="icon"
								onClick={handleDismissGettingStarted}
								className="h-8 w-8">
								<X className="h-4 w-4" />
							</Button>
						</CardHeader>
						<CardContent className="pt-0">
							<div className="space-y-4">
								<div>
									<h4 className="font-semibold text-base mb-1">
										{currentStepData.title}
									</h4>
									<p className="text-sm text-muted-foreground">
										{currentStepData.description}
									</p>
								</div>
								<Button
									onClick={currentStepData.action}
									className="w-full sm:w-auto"
									size="default">
									<currentStepData.buttonIcon className="mr-2 h-4 w-4" />
									{currentStepData.buttonText}
								</Button>
							</div>
						</CardContent>
					</Card>
				</motion.div>
			)}

			{/* Hidden Data Banner for Free Users on Dashboard */}
			{data && !data.isPro && (data.totalHiddenCountThisPeriod ?? 0) > 0 && (
				<Alert
					variant="default"
					className="mb-6 bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700">
					<Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
					<AlertTitle className="font-semibold text-amber-700 dark:text-amber-300">
						{t('dashboard.unlockFullFeedbackPicture')}
					</AlertTitle>
					<AlertDescription className="text-amber-600 dark:text-amber-400">
						{t('dashboard.collectingValuableInsights')}{' '}
						{t('dashboard.responsesHidden', {
							count: data.totalHiddenCountThisPeriod ?? 0,
							context: currentProgram
								? t('dashboard.forProgram', { name: programNameToDisplay })
								: t('dashboard.acrossPrograms')
						})}
						<Button
							variant="link"
							className="p-0 h-auto text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1 font-medium"
							onClick={() => navigate('/settings?tab=billing')}>
							{t('dashboard.upgradeToProToViewAll')}
						</Button>
					</AlertDescription>
				</Alert>
			)}

			<div className="w-full">
				<DashboardInbox isPro={data.isPro} />
			</div>

			{/* Feedback Timeline Chart - Full width below map */}
			<div className="w-full mt-6">
				<FeedbackTimelineChart
					programId={effectiveProgramId}
					title={t('dashboard.feedbackOverTime')}
					height={400}
					className="w-full"
				/>
			</div>

			{/* Bottom Row: Tables & Needs Attention */}
			{/* <div className="grid gap-6 sm:gap-8 grid-cols-1">
				<Card>
					<CardHeader>
						<CardTitle className="text-lg font-semibold">
							{t('dashboard.recentActivity')}
						</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						
						{(data.recentActivity?.length ?? 0) === 0 ? (
							<div className="text-center py-12 text-muted-foreground space-y-4">
								{gettingStartedDismissed && (data.totalFeedback ?? 0) === 0 ? (
									<>
										<MessagesSquare className="mx-auto h-12 w-12 text-primary/30" />
										<div>
											<p className="font-medium">{t('dashboard.noFeedbackYet')}</p>
											<p className="text-sm">
												{t('dashboard.createWidgetAndStart')}
											</p>
										</div>
										<Button onClick={() => navigate('/widgets/new')}>
											<Layout className="mr-2 h-4 w-4" />
											{t('dashboard.createWidget')}
										</Button>
									</>
								) : (
									<>
										<MessagesSquare className="mx-auto h-12 w-12 text-muted-foreground/50" />
										<p>{t('dashboard.noActivityInTimePeriod')}</p>
									</>
								)}
							</div>
						) : (
							<RecentActivityTable
								items={data.recentActivity ?? []}
								onItemClick={handleItemClick}
							/>
						)}
					</CardContent>
				</Card>

				
				<div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
					<NPSChart timePeriod={timePeriod} programId={effectiveProgramId} />
					<FeedbackDistributionChart
						data={data.feedbackDistribution ?? []}
					/>
				</div>

				
				{selectedBugId && (
					<BugReportDetailView
						bugId={selectedBugId}
						onClose={() => setSelectedBugId(null)}
					/>
				)}
				{selectedFeatureId && (
					<FeatureRequestDetailView
						featureId={selectedFeatureId}
						onClose={() => setSelectedFeatureId(null)}
					/>
				)}
				{selectedResponseId && (
					<ResponseDetailView
						responseId={selectedResponseId}
						onClose={() => setSelectedResponseId(null)}
					/>
				)}
			</div> */}
			</div>
		</div>
	)
}
